import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Header from './Header';
import SideNav from './SideNav';
import PageHeader from './PageHeader';
import usePageTimeTracker from '../hooks/usePageTimeTracker';
import './AssessmentList.css';

const API_BASE = process.env.REACT_APP_API_URL || '/lb-api';

const LANG_NAMES = { en: 'English', fr: 'French', de: 'German', es: 'Spanish', it: 'Italian', ja: 'Japanese' };

// Proficiency ordering for the "Highest/Lowest Proficiency" sort + level arrows.
const LEVEL_ORDER = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2', 'N5', 'N4', 'N3', 'N2', 'N1'];
const levelRank = (lvl) => LEVEL_ORDER.indexOf(String(lvl || '').toUpperCase());
function levelDirection(prev, next) {
  const a = levelRank(prev), b = levelRank(next);
  if (a < 0 || b < 0 || a === b) return 'same';
  return b > a ? 'up' : 'down';
}

function formatDateTime(iso) {
  if (!iso) return '-';
  const d = new Date(iso);
  const date = d.toLocaleDateString('en-GB');
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true }).toLowerCase().replace(' ', '');
  return `${date} at ${time}`;
}

function formatDuration(secs) {
  const s = Number(secs) || 0;
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')} min`;
}

const SORT_OPTIONS = [
  { key: 'latest', label: 'Latest' },
  { key: 'oldest', label: 'Oldest' },
  { key: 'lang', label: 'Languages Alphabetically' },
  { key: 'prof-high', label: 'Highest Proficiency' },
  { key: 'prof-low', label: 'Lowest Proficiency' },
];

// Time-period options match the Dashboard's range selector. `days` is the lookback
// window used to filter rows by completedAt.
const PERIOD_OPTIONS = [
  { key: 'week', label: 'Last Week', days: 7 },
  { key: 'month', label: 'Last Month', days: 30 },
  { key: 'quarter', label: 'Last 3 Months', days: 90 },
  { key: '6months', label: 'Last 6 Months', days: 180 },
];
const VALID_PERIODS = PERIOD_OPTIONS.map(p => p.key);

const AssessmentList = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  usePageTimeTracker('assessment-list', user);

  // The language selected in the Dashboard snapshot: 'all' -> show all languages,
  // a specific code (e.g. 'de') -> show only that language's attempts.
  const incomingLang = location.state && location.state.lang ? location.state.lang : 'all';
  const initialLangFilter = incomingLang !== 'all' ? [incomingLang] : [];

  // Default the time-period filter to whatever period was selected on the Dashboard.
  const incomingRange = location.state && VALID_PERIODS.includes(location.state.range) ? location.state.range : '6months';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Applied state (what the table actually uses).
  const [sortKey, setSortKey] = useState('latest');
  const [period, setPeriod] = useState(incomingRange);
  const [langFilter, setLangFilter] = useState(initialLangFilter); // [] = all languages

  // Draft state inside the open popovers (applied on "Apply").
  const [draftSort, setDraftSort] = useState('latest');
  const [draftPeriod, setDraftPeriod] = useState(incomingRange);
  const [draftLangs, setDraftLangs] = useState(initialLangFilter);
  const [sortSearch, setSortSearch] = useState('');

  const [showSort, setShowSort] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const sortRef = useRef(null);
  const filterRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API_BASE}/post-assessment/history-all?_t=${Date.now()}`, { withCredentials: true });
        if (!cancelled && res.data && res.data.success) setRows(res.data.history || []);
      } catch (e) {
        if (!cancelled) setRows([]);
      }
      if (!cancelled) setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (sortRef.current && !sortRef.current.contains(e.target)) setShowSort(false);
      if (filterRef.current && !filterRef.current.contains(e.target)) setShowFilter(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const languages = Array.from(new Set(rows.map(r => r.language_code)));

  // Count of active filters for the "Apply Filters (N)" label.
  const draftFilterCount = (draftPeriod !== 'all' ? 1 : 0) + draftLangs.length;

  const openSortPopover = () => { setDraftSort(sortKey); setSortSearch(''); setShowSort(s => !s); setShowFilter(false); };
  const openFilterPopover = () => { setDraftPeriod(period); setDraftLangs(langFilter); setShowFilter(s => !s); setShowSort(false); };

  const applySort = () => { setSortKey(draftSort); setShowSort(false); };
  const applyFilter = () => { setPeriod(draftPeriod); setLangFilter(draftLangs); setShowFilter(false); };

  const toggleDraftLang = (code) => {
    setDraftLangs(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
  };

  // Build the visible rows: time-period cutoff + language filter + sort.
  const now = Date.now();
  const periodDays = (PERIOD_OPTIONS.find(p => p.key === period) || {}).days || 0;
  const periodCut = periodDays === 0 ? 0 : now - periodDays * 24 * 3600 * 1000;
  const visible = rows
    .filter(r => periodDays === 0 || new Date(r.startedAt || r.completedAt).getTime() >= periodCut)
    .filter(r => langFilter.length === 0 || langFilter.includes(r.language_code))
    .slice()
    .sort((a, b) => {
      const aDate = new Date(a.startedAt || a.completedAt);
      const bDate = new Date(b.startedAt || b.completedAt);
      switch (sortKey) {
        case 'oldest': return aDate - bDate;
        case 'lang': {
          // Primary: language name A-Z. Secondary: proficiency (assigned) level.
          const byLang = (LANG_NAMES[a.language_code] || a.language_code).localeCompare(LANG_NAMES[b.language_code] || b.language_code);
          if (byLang !== 0) return byLang;
          return levelRank(a.assignedLevel) - levelRank(b.assignedLevel);
        }
        case 'prof-high': return levelRank(b.assignedLevel) - levelRank(a.assignedLevel);
        case 'prof-low': return levelRank(a.assignedLevel) - levelRank(b.assignedLevel);
        default: return bDate - aDate; // latest
      }
    });

  const openSummary = (row) => {
    // Open the specific attempt's summary in a new tab. Pass identifiers as query
    // params (location.state doesn't survive window.open).
    const url = `/post-assessment?viewSummary=1&sessionId=${encodeURIComponent(row.session_id)}&lang=${encodeURIComponent(row.language_code)}`;
    window.open(url, '_blank', 'noopener');
  };

  const visibleSortOptions = SORT_OPTIONS.filter(o => o.label.toLowerCase().includes(sortSearch.toLowerCase()));

  return (
    <div className="al-page">
      <Header user={user} />

      <div className="nav-header-container">
        <SideNav />
        <PageHeader
          breadcrumbs={[
            { label: 'Home', path: '/', current: false },
            { label: 'Dashboard', path: '/dashboard', current: false },
            { label: 'Assessment Summary', current: true },
          ]}
          rightContent={<></>}
        />
      </div>

      <main className="al-main">
        <div className="al-head">
          <div className="al-head-left">
            <button className="al-back" onClick={() => navigate('/dashboard', { state: { scrollTo: 'assessments', range: incomingRange } })} aria-label="Back to Dashboard">
              <img src="/images/leftbackArrow.svg" alt="" width="24" height="24" />
            </button>
            <h1 className="al-title">Assessment List</h1>
          </div>

          <div className="al-controls">
            {/* Sort */}
            <div className="al-control" ref={sortRef}>
              <button className="al-control-btn" onClick={openSortPopover}>
                <img src="/images/sort.svg" alt="" width="14" height="14" onError={e => { e.target.style.display = 'none'; }} /> Sort
                <img className="al-caret-img" src="/images/DropdownArrowsort.svg" alt="" width="12" height="12" onError={e => { e.target.style.display = 'none'; }} />
              </button>
              {showSort && (
                <div className="al-pop">
                  <div className="al-pop-search">
                    <img src="/images/searchicon.svg" alt="" width="14" height="14" onError={e => { e.target.style.display = 'none'; }} />
                    <input value={sortSearch} onChange={e => setSortSearch(e.target.value)} placeholder="Search" />
                  </div>
                  {visibleSortOptions.map(o => (
                    <label key={o.key} className="al-opt">
                      <input type="radio" name="al-sort" checked={draftSort === o.key} onChange={() => setDraftSort(o.key)} />
                      {o.label}
                    </label>
                  ))}
                  <button className="al-apply" onClick={applySort}>Apply Filter (1)</button>
                </div>
              )}
            </div>

            {/* Filter */}
            <div className="al-control" ref={filterRef}>
              <button className="al-control-btn" onClick={openFilterPopover}>
                <img src="/images/Filter.svg" alt="" width="14" height="14" onError={e => { e.target.style.display = 'none'; }} /> Filter
                <img className="al-caret-img" src="/images/DropdownArrowsort.svg" alt="" width="12" height="12" onError={e => { e.target.style.display = 'none'; }} />
              </button>
              {showFilter && (
                <div className="al-pop">
                  <div className="al-pop-group-title">Filter by time period</div>
                  {PERIOD_OPTIONS.map(p => (
                    <label key={p.key} className="al-opt">
                      <input type="checkbox" checked={draftPeriod === p.key} onChange={() => setDraftPeriod(p.key)} />
                      {p.label}
                    </label>
                  ))}
                  <div className="al-pop-group-title">Filter with Languages practiced</div>
                  {languages.map(code => (
                    <label key={code} className="al-opt">
                      <input type="checkbox" checked={draftLangs.includes(code)} onChange={() => toggleDraftLang(code)} />
                      {LANG_NAMES[code] || code}
                    </label>
                  ))}
                  <button className="al-apply" onClick={applyFilter}>Apply Filters ({draftFilterCount})</button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="al-table-wrap">
          <table className="al-table">
            <thead>
              <tr>
                <th>Language</th>
                <th>Date And Time</th>
                <th>Level achieved</th>
                <th>Time taken</th>
                <th className="al-col-summary">Summary</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="al-empty">Loading...</td></tr>
              ) : visible.length === 0 ? (
                <tr><td colSpan={5} className="al-empty">No completed assessments yet.</td></tr>
              ) : (
                visible.map((r, i) => {
                  const dir = levelDirection(r.previousLevel, r.assignedLevel);
                  const levelIcon = dir === 'up' ? '/images/uplevelgreen.svg' : dir === 'down' ? '/images/downgradeIcon.svg' : '/images/samelevelicon.svg';
                  return (
                    <tr key={`${r.session_id || 'row'}-${r.attempt_number}-${i}`}>
                      <td>
                        <span className="al-lang">
                          <img src={`/images/flag-${String(r.language_code || '').toLowerCase()}.svg`} alt="" width="18" height="18" onError={e => { e.target.style.display = 'none'; }} />
                          {LANG_NAMES[r.language_code] || r.language_code}
                        </span>
                      </td>
                      <td>{formatDateTime(r.startedAt || r.completedAt)}</td>
                      <td>
                        <span className={`al-level al-level-${dir}`}>
                          {levelIcon && <img className="al-level-icon" src={levelIcon} alt="" width="14" height="14" onError={e => { e.target.style.display = 'none'; }} />}
                          {r.previousLevel} - {r.assignedLevel}
                        </span>
                      </td>
                      <td>{formatDuration(r.timeTaken)}</td>
                      <td className="al-col-summary">
                        <button className="al-view-link" onClick={() => openSummary(r)}>
                          View Summary <span aria-hidden="true" className="al-ext">&#8599;</span>
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
};

export default AssessmentList;
